import ExcelJS from 'exceljs';
import ptcImage from '../../../imagenes/ptc.jpg';
import {
  BORDER,
  safe,
  setColWidths,
  workbookToDownload,
} from '../../documentos/commercialExcelUtils';
import { PK_DECLA_COMPANY_LINES, PK_DECLA_TITLE, writePkDeclaStaticBlockFixed } from './pkDeclaStaticContent';
import { pkDeclaContainerList, pkDeclaExcelLayoutRows } from './pkDeclaLayout';
import { writePkDeclaFooter } from './pkDeclaFooter';

const FONT = { name: 'Arial', size: 8 };

/** Anchos hoja PK Decla (plantilla Invoice 5019). */
const COL_WIDTHS = [17.44, 15.55, 40.11, 12, 12, 12];

const LABEL_VESSEL = 'Vessel Name';
const LABEL_CONSIGNEE = 'Consigned identifier';

const ROW_TITLE = 13;
const ROW_VESSEL = 14;
const ROW_CONSIGNEE_LABEL = 15;
const ROW_ADDRESS_START = 16;
const ROW_ADDRESS_END = 19;
const ROW_TABLE_HEAD = 21;
const ROW_TABLE_DATA_START = 22;

const PAGE_SETUP = {
  orientation: 'portrait',
  paperSize: 9,
  fitToPage: true,
  fitToWidth: 1,
  fitToHeight: 1,
  margins: { left: 0.35, right: 0.35, top: 0.25, bottom: 0.25, header: 0, footer: 0 },
};

async function imageUrlToDataUrl(url, mimeType = 'image/jpeg') {
  const response = await fetch(url);
  if (!response.ok) throw new Error('No se pudo cargar el logo PTC.');
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:${mimeType};base64,${btoa(binary)}`;
}

function setCell(ws, r, c, value, opts = {}) {
  const cell = ws.getCell(r, c);
  cell.value = value == null || value === '' ? null : value;
  cell.font = { ...FONT, ...(opts.bold ? { bold: true } : {}) };
  cell.alignment = {
    vertical: opts.align?.vertical ?? 'top',
    wrapText: opts.wrap !== false,
    horizontal: opts.align?.horizontal ?? 'left',
  };
  if (opts.border) cell.border = BORDER;
}

function mergeWrite(ws, r1, c1, r2, c2, text, opts = {}) {
  if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  setCell(ws, r1, c1, text, opts);
}

function writeCenteredColC(ws, r, text, rowH = 10.5) {
  setCell(ws, r, 3, text, {
    align: { horizontal: 'center', vertical: 'middle' },
  });
  ws.getRow(r).height = rowH;
}

function writeHeaderFixed(ws, params) {
  setCell(ws, ROW_VESSEL, 1, LABEL_VESSEL, { align: { vertical: 'middle' } });
  writeCenteredColC(ws, ROW_VESSEL, safe(params.vesselName).trim() || null, 10.5);

  if (safe(params.consigneeName).trim()) {
    setCell(ws, ROW_CONSIGNEE_LABEL, 1, LABEL_CONSIGNEE, { align: { vertical: 'middle' } });
    writeCenteredColC(ws, ROW_CONSIGNEE_LABEL, params.consigneeName, 10.5);
  }

  const addr = (params.consigneeAddressLines || [])
    .map((l) => safe(l).trim())
    .filter(Boolean)
    .slice(0, ROW_ADDRESS_END - ROW_ADDRESS_START + 1);

  addr.forEach((line, i) => {
    writeCenteredColC(ws, ROW_ADDRESS_START + i, line, 10);
  });
}

/** Todas las filas de contenedor (sin tope); devuelve última fila de datos. */
function writeContainersTable(ws, containers, bl, inv) {
  const headers = ['CONTAINERS', 'BL', 'Invoice'];
  [2, 3, 4].forEach((c, i) => {
    setCell(ws, ROW_TABLE_HEAD, c, headers[i], {
      bold: true,
      border: true,
      align: { horizontal: 'center', vertical: 'middle' },
    });
  });
  ws.getRow(ROW_TABLE_HEAD).height = 9.5;

  const list = (containers || []).map((c) => safe(c).trim()).filter(Boolean);
  const rows = list.length > 0 ? list : [''];
  const invVal = inv ? Number(inv) || inv : '';

  rows.forEach((cont, i) => {
    const r = ROW_TABLE_DATA_START + i;
    setCell(ws, r, 2, cont, {
      border: true,
      align: { horizontal: 'center', vertical: 'middle' },
    });
    setCell(ws, r, 3, bl, { border: true, align: { horizontal: 'center', vertical: 'middle' } });
    setCell(ws, r, 4, invVal, { border: true, align: { horizontal: 'center', vertical: 'middle' } });
    ws.getRow(r).height = 9.5;
  });

  return ROW_TABLE_DATA_START + rows.length - 1;
}

function writeMerchandise(ws, merchRow, lines) {
  const merch = (lines || []).slice(0, 4);
  while (merch.length < 4) merch.push('');
  ws.getRow(merchRow - 1).height = 8;
  merch.forEach((line, i) => {
    const r = merchRow + i;
    const text = safe(line).trim();
    mergeWrite(ws, r, 1, r, 6, text || null, {
      align: { horizontal: 'left', vertical: 'top' },
    });
    ws.getRow(r).height = text.length > 28 ? 13.5 : 11.5;
  });
}

export function pkDeclaExcelFileName(params) {
  const inv = safe(params?.numeroInvoice).replace(/\W/g, '_') || 'export';
  return `PK_Decla_${inv}.xlsx`;
}

export async function buildPkDeclaExcelWorkbook(params) {
  const containerList = pkDeclaContainerList(params.containers);
  const layout = pkDeclaExcelLayoutRows(containerList.length);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('PK Decla', { pageSetup: PAGE_SETUP });
  setColWidths(ws, COL_WIDTHS);

  for (let r = 1; r <= layout.printLastRow; r += 1) {
    ws.getRow(r).height = 7.5;
  }
  [14, 12, 10, 8, 6, 5].forEach((h, i) => {
    ws.getRow(i + 1).height = h;
  });

  const logoId = wb.addImage({
    base64: await imageUrlToDataUrl(ptcImage),
    extension: 'jpeg',
  });
  ws.addImage(logoId, {
    tl: { col: 0, row: 0 },
    ext: { width: 88, height: 72 },
    editAs: 'oneCell',
  });

  PK_DECLA_COMPANY_LINES.forEach((line, i) => {
    const r = 7 + i;
    setCell(ws, r, 1, line, { bold: i === 0 });
    ws.getRow(r).height = i === 0 ? 12 : 10;
  });

  mergeWrite(ws, ROW_TITLE, 1, ROW_TITLE, 6, PK_DECLA_TITLE, {
    bold: true,
    align: { horizontal: 'center', vertical: 'middle' },
  });
  ws.getRow(ROW_TITLE).height = 14;

  writeHeaderFixed(ws, params);

  writeContainersTable(ws, containerList, params.blNumber || '', params.numeroInvoice || '');
  writeMerchandise(ws, layout.merchRow, params.merchandiseLines || []);

  const helpers = {
    mergeBlock: (r1, c1, r2, c2, text, opts) => mergeWrite(ws, r1, c1, r2, c2, text, opts),
    setRowHeight: (row, h) => {
      ws.getRow(row).height = h;
    },
    writePair: (r, a, c) => {
      setCell(ws, r, 1, a);
      setCell(ws, r, 3, c);
    },
  };

  writePkDeclaStaticBlockFixed(ws, helpers, layout.staticStart);
  await writePkDeclaFooter(ws, wb, layout.footerSignedRow, params.issueDateIso);

  ws.pageSetup.printArea = `A1:F${layout.printLastRow}`;

  return wb;
}

export async function generatePkDeclaExcel(params) {
  const wb = await buildPkDeclaExcelWorkbook(params);
  await workbookToDownload(wb, pkDeclaExcelFileName(params));
}
